import React, { useState } from "react";
import {
  Map,
  ShieldCheck,
  Activity,
  CheckCircle2,
  Layers,
  Glasses,
  Users,
  Building2,
  ChevronRight,
  Menu,
  X,
  Cpu,
  Globe,
  HardHat,
  Sparkles,
  Wand2,
  Loader2,
  ShoppingCart,
  Plus,
  Minus,
  Package,
  Droplets,
  Lightbulb,
  Paintbrush,
  FileText,
  MessageSquare,
  TrendingUp,
  Clock,
} from "lucide-react";

const callGemini = async (prompt, systemInstruction) => {
  const apiKey = "AIzaSyAWf8sNUsfq6ZzG80-ERXJD38CJ11bIhaQ";
  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`;

  const payload = {
    contents: [{ parts: [{ text: prompt }] }],
    systemInstruction: { parts: [{ text: systemInstruction }] },
    generationConfig: {
      temperature: 0.7,
    },
  };

  const delay = (ms) => new Promise((res) => setTimeout(res, ms));
  const retries = [1000, 2000, 4000, 8000, 16000];

  for (let i = 0; i < retries.length; i++) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      return (
        result.candidates?.[0]?.content?.parts?.[0]?.text ||
        "No response generated."
      );
    } catch (error) {
      if (i === retries.length - 1) throw error;
      await delay(retries[i]);
    }
  }
};

const ProductCard = ({ product, onAdd }) => {
  const [qty, setQty] = useState(product.calculatedQty || 1);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    onAdd({ ...product, qty });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const getCategoryIcon = (category) => {
    const cat = (category || "").toLowerCase();
    if (
      cat.includes("plumb") ||
      cat.includes("water") ||
      cat.includes("fixture")
    )
      return (
        <Droplets className="h-10 w-10 text-gray-500 relative z-10 group-hover:text-white transition-colors duration-300" />
      );
    if (cat.includes("light"))
      return (
        <Lightbulb className="h-10 w-10 text-gray-500 relative z-10 group-hover:text-white transition-colors duration-300" />
      );
    if (
      cat.includes("wall") ||
      cat.includes("paint") ||
      cat.includes("finish") ||
      cat.includes("cover")
    )
      return (
        <Paintbrush className="h-10 w-10 text-gray-500 relative z-10 group-hover:text-white transition-colors duration-300" />
      );
    return (
      <Package className="h-10 w-10 text-gray-500 relative z-10 group-hover:text-white transition-colors duration-300" />
    );
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-3xl overflow-hidden group hover:bg-[#161618] hover:border-white/20 transition-all duration-300 flex flex-col">
      {/* 3D Visualizer Placeholder */}
      <div className="h-40 bg-black border-b border-white/5 flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:12px_12px]"></div>
        {getCategoryIcon(product.category)}
        <span className="absolute bottom-3 right-4 text-[10px] text-gray-500 font-medium tracking-widest uppercase">
          3D Asset
        </span>
      </div>

      {/* Product Details */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-2">
          <div>
            <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mb-1.5">
              {product.brand}
            </div>
            <h5 className="text-white font-medium text-base leading-tight pr-2">
              {product.name}
            </h5>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-white font-medium text-base">
              ${product.price}
            </div>
            <div className="text-[10px] text-gray-500 uppercase">
              / {product.unit}
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-500 mb-6 flex-grow font-light leading-relaxed">
          SKU: <span className="text-gray-400">{product.sku}</span>
          <br />
          Lead Time: <span className="text-gray-400">{product.leadTime}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-auto">
          <div className="flex items-center bg-black border border-white/10 rounded-full px-2 py-1">
            <button
              onClick={() => setQty(Math.max(1, qty - 1))}
              className="p-1.5 text-gray-400 hover:text-white transition-colors"
            >
              <Minus className="h-3 w-3" />
            </button>
            <span className="w-8 text-center text-sm font-medium text-white">
              {qty}
            </span>
            <button
              onClick={() => setQty(qty + 1)}
              className="p-1.5 text-gray-400 hover:text-white transition-colors"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>

          <button
            onClick={handleAdd}
            className={`flex-grow py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
              added
                ? "bg-white text-black"
                : "bg-[#2997ff] text-white hover:bg-[#147ce5]"
            }`}
          >
            {added ? "Added" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [activeAITab, setActiveAITab] = useState("feasibility"); // feasibility, material, proposal, code
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [cart, setCart] = useState([]);

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.id === item.id);
      if (existing) {
        return prev.map((i) =>
          i.id === item.id ? { ...i, qty: i.qty + item.qty } : i
        );
      }
      return [...prev, item];
    });
  };

  const handleAIGeneration = async () => {
    if (!aiPrompt.trim()) return;
    setIsGenerating(true);
    setAiResponse(null);

    try {
      let systemPrompt = "";

      if (activeAITab === "feasibility") {
        systemPrompt = `You are the iDESIGN DEVELOP AI. The user is providing a location and project description. 
        Provide a 'Site Feasibility & Compliance Check'.
        You MUST return ONLY a valid JSON object with the following structure. Do NOT wrap it in markdown block quotes.
        {
          "analysis": "Brief professional summary of site constraints and feasibility (under 60 words).",
          "zoningDetails": {
            "code": "e.g., R-2",
            "far": "e.g., 0.8",
            "maxHeight": "e.g., 35ft",
            "setbacks": "e.g., Front: 20', Sides: 5'"
          },
          "recommendedTier": "The Refresh, The Modifier, or The Visionary",
          "nextSteps": [
            "Actionable step 1",
            "Actionable step 2",
            "Actionable step 3"
          ]
        }`;
      } else if (activeAITab === "material") {
        systemPrompt = `You are the iDESIGN DEVELOP AI Material Specifier. The user is providing a design style or room description. Generate a curated 'Material & Fixture List' using premium brands (e.g., Kohler, Phillip Jefferies).
        You MUST return ONLY a valid JSON object with the following structure. Do NOT wrap it in markdown block quotes.
        {
          "collectionName": "e.g., Moody Industrial Luxury",
          "description": "Brief description of the aesthetic",
          "products": [
            {
              "id": "unique-sku-1",
              "brand": "e.g., Kohler",
              "name": "e.g., Purist Wall-Mount Faucet",
              "sku": "e.g., K-T14414-4-BL",
              "price": 450,
              "unit": "item",
              "calculatedQty": 2,
              "leadTime": "2 weeks",
              "category": "Plumbing"
            }
          ]
        }
        Generate 3-4 highly curated products matching the user's prompt.`;
      } else if (activeAITab === "proposal") {
        systemPrompt = `You are the iDESIGN DEVELOP AI Investment Pitch Generator. The user is providing a project concept or description. Generate a professional, compelling Investment Teaser / Project Proposal.
        You MUST return ONLY a valid JSON object with the following structure. Do NOT wrap it in markdown block quotes.
        {
          "projectTitle": "Catchy, professional title",
          "executiveSummary": "A compelling 2-3 sentence overview of the project's value proposition.",
          "targetDemographic": "Who this project is built for.",
          "estimatedTimeline": "e.g., 14-18 Months from acquisition to completion.",
          "roiHighlights": [
            "Financial or strategic highlight 1",
            "Financial or strategic highlight 2",
            "Financial or strategic highlight 3"
          ]
        }`;
      } else if (activeAITab === "code") {
        systemPrompt = `You are the iDESIGN DEVELOP AI Zoning & Code Consultant. The user will ask a specific question about building codes, zoning laws, or permitting. 
        Provide a concise, highly professional, and accurate response based on standard US building codes (or the specific municipality if mentioned). Keep the tone authoritative yet helpful. Keep it under 100 words.`;
      }

      const result = await callGemini(aiPrompt, systemPrompt);

      // Parse JSON for tabs that expect it
      if (
        activeAITab === "feasibility" ||
        activeAITab === "material" ||
        activeAITab === "proposal"
      ) {
        const cleanedResult = result
          .replace(/```json/gi, "")
          .replace(/```/g, "")
          .trim();
        setAiResponse(JSON.parse(cleanedResult));
      } else {
        // Plain text response for the code consultant
        setAiResponse(result);
      }
    } catch (error) {
      // Fallback error states
      if (activeAITab === "feasibility") {
        setAiResponse({
          analysis:
            "System Error: Unable to connect to the intelligence layer or parse zoning data.",
          zoningDetails: {
            code: "N/A",
            far: "N/A",
            maxHeight: "N/A",
            setbacks: "N/A",
          },
          recommendedTier: "N/A",
          nextSteps: ["Please try submitting your request again."],
        });
      } else if (activeAITab === "material") {
        setAiResponse({
          collectionName: "System Error",
          description:
            "Unable to connect to the intelligence layer or parse material data. Please try submitting your request again.",
          products: [],
        });
      } else if (activeAITab === "proposal") {
        setAiResponse({
          projectTitle: "Error Generating Proposal",
          executiveSummary: "Unable to connect to the intelligence layer.",
          targetDemographic: "N/A",
          estimatedTimeline: "N/A",
          roiHighlights: ["Please try again."],
        });
      } else {
        setAiResponse(
          "System Error: Unable to consult the code database. Please try your query again."
        );
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const getPlaceholderText = () => {
    switch (activeAITab) {
      case "feasibility":
        return "Describe your project location and vision (e.g., '123 Main St, Austin TX - 2 story modern ADU').";
      case "material":
        return "Describe the room and desired aesthetic (e.g., 'Primary bathroom, moody industrial luxury').";
      case "proposal":
        return "Describe your development idea for an investor pitch (e.g., '3-story mixed-use building in downtown Miami').";
      case "code":
        return "Ask a specific zoning or building code question (e.g., 'What is the standard rear setback for a residential property in California?').";
      default:
        return "Enter prompt...";
    }
  };

  const getButtonText = () => {
    switch (activeAITab) {
      case "feasibility":
        return "Analyze Site Constraints ✨";
      case "material":
        return "Generate Material List ✨";
      case "proposal":
        return "Generate Pitch Deck ✨";
      case "code":
        return "Consult Zoning Expert ✨";
      default:
        return "Submit ✨";
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-300 font-sans antialiased selection:bg-white selection:text-black">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-black/70 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer">
              <span className="font-semibold text-xl tracking-tight text-white">
                iDESIGN<span className="text-gray-400">DEVELOP</span>
              </span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#intelligence"
                className="text-xs font-medium text-gray-300 hover:text-white transition-colors"
              >
                Intelligence
              </a>
              <a
                href="#tiers"
                className="text-xs font-medium text-gray-300 hover:text-white transition-colors"
              >
                Tiers
              </a>
              <a
                href="#addons"
                className="text-xs font-medium text-gray-300 hover:text-white transition-colors"
              >
                Hardware
              </a>
              <a
                href="#municipal"
                className="text-xs font-medium text-gray-300 hover:text-white transition-colors"
              >
                Municipal
              </a>
              <button
                onClick={() => setShowAIModal(true)}
                className="bg-white text-black px-5 py-2 rounded-full font-medium text-xs hover:bg-gray-200 transition-colors"
              >
                Start Analysis
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-300 hover:text-white"
              >
                {isMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-[#111111] border-b border-white/10">
            <div className="px-4 pt-2 pb-4 space-y-1">
              <a
                href="#intelligence"
                className="block py-3 text-sm font-medium text-gray-300 hover:text-white border-b border-white/5"
              >
                Intelligence Layer
              </a>
              <a
                href="#tiers"
                className="block py-3 text-sm font-medium text-gray-300 hover:text-white border-b border-white/5"
              >
                Service Tiers
              </a>
              <a
                href="#addons"
                className="block py-3 text-sm font-medium text-gray-300 hover:text-white border-b border-white/5"
              >
                Hardware & Add-Ons
              </a>
              <a
                href="#municipal"
                className="block py-3 text-sm font-medium text-gray-300 hover:text-white"
              >
                Municipal Portal
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 lg:pt-56 lg:pb-40 overflow-hidden flex flex-col items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/80 to-black"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-gray-300 text-[10px] font-semibold tracking-widest uppercase mb-8 backdrop-blur-md">
            <Globe className="h-3.5 w-3.5 text-white" /> AI-Powered Real Estate
            Engine
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-semibold text-white tracking-tighter leading-tight mb-6">
            Develop.
            <br />
            <span className="text-gray-500">Without the delay.</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 font-light mb-12 max-w-2xl mx-auto tracking-tight leading-relaxed">
            The first AI-powered engine built to collapse the time between site
            acquisition and construction.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setShowAIModal(true);
                setActiveAITab("feasibility");
              }}
              className="bg-white text-black px-8 py-4 rounded-full font-medium text-base hover:bg-gray-200 transition-all flex items-center justify-center"
            >
              Start Free Analysis
            </button>
            <button
              onClick={() => {
                setShowAIModal(true);
                setActiveAITab("proposal");
              }}
              className="bg-[#1c1c1e] text-white border border-white/10 px-8 py-4 rounded-full font-medium text-base hover:bg-[#2c2c2e] transition-all flex items-center justify-center gap-2"
            >
              Generate Project Pitch{" "}
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </div>
      </section>

      {/* Motto Section */}
      <section className="py-20 border-y border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[10px] font-semibold tracking-[0.2em] text-gray-500 mb-6 uppercase">
            Empowering the Designer and Developer with AI
          </p>
          <h2 className="text-2xl md:text-3xl font-light text-white tracking-widest uppercase">
            Desire <span className="text-gray-600 mx-3">|</span> Design{" "}
            <span className="text-gray-600 mx-3">|</span> Develop
          </h2>
          <p className="mt-6 text-gray-500 text-xs font-medium tracking-[0.15em] uppercase">
            Conceptualize &nbsp;&middot;&nbsp; Coordinate &nbsp;&middot;&nbsp;
            Collaborate &nbsp;&middot;&nbsp; Construct &nbsp;&middot;&nbsp;
            Curate
          </p>
        </div>
      </section>

      {/* The Intelligence Layer */}
      <section id="intelligence" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6">
              The Intelligence Layer.
            </h2>
            <p className="text-xl text-gray-400 font-light max-w-3xl mx-auto tracking-tight">
              Designs that know the rules before a single line is drawn.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#161618] transition-colors">
              <Map className="h-10 w-10 text-white mb-8" />
              <h3 className="text-2xl font-semibold text-white mb-4 tracking-tight">
                Instant Analysis
              </h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Enter any parcel number to pull GIS mapping and high-resolution
                LiDAR topography instantly. Know exactly what the rules are
                before you spend a dime.
              </p>
            </div>

            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#161618] transition-colors">
              <ShieldCheck className="h-10 w-10 text-white mb-8" />
              <h3 className="text-2xl font-semibold text-white mb-4 tracking-tight">
                Regulatory Guardrails
              </h3>
              <p className="text-gray-400 font-light leading-relaxed">
                Our AI integrates local zoning ordinances and height
                restrictions to generate a transparent "Building Envelope". If
                you can't build it there, it won't draw it.
              </p>
            </div>

            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#161618] transition-colors relative overflow-hidden">
              <Activity className="h-10 w-10 text-white mb-8" />
              <h3 className="text-2xl font-semibold text-white mb-4 tracking-tight">
                Real-Time Compliance
              </h3>
              <p className="text-gray-400 font-light leading-relaxed mb-8">
                The generative engine calculates metrics as you design to ensure
                every structure is legally buildable.
              </p>
              <div className="bg-black p-5 rounded-2xl border border-white/10 font-mono text-xs">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Floor Area Ratio</span>
                  <span className="text-white">2.5 / 3.0</span>
                </div>
                <div className="w-full bg-[#1c1c1e] rounded-full h-1 mb-5">
                  <div className="bg-white h-1 rounded-full w-[83%]"></div>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Permeable Cover</span>
                  <span className="text-white">45% / 50%</span>
                </div>
                <div className="w-full bg-[#1c1c1e] rounded-full h-1">
                  <div className="bg-white h-1 rounded-full w-[90%]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers */}
      <section
        id="tiers"
        className="py-32 bg-[#050505] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24">
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6">
              Subscriptions.
            </h2>
            <p className="text-xl text-gray-400 font-light max-w-2xl mx-auto tracking-tight">
              From refreshing a room to breaking ground on a commercial lot,
              select the precision you need.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-center">
            {/* Tier 1 */}
            <div className="bg-[#111111] rounded-[2rem] border border-white/5 p-10 flex flex-col hover:bg-[#151515] transition-colors h-[600px]">
              <div className="mb-8">
                <h3 className="text-3xl font-semibold text-white mb-2 tracking-tight">
                  The Refresh
                </h3>
                <div className="text-gray-400 text-sm font-light mb-6">
                  Interiors & Exterior Updates
                </div>
                <div className="flex items-baseline text-4xl font-semibold text-white tracking-tighter">
                  $3,000
                  <span className="ml-1 text-base font-light text-gray-500 tracking-normal">
                    /project
                  </span>
                </div>
              </div>
              <ul className="space-y-5 mb-8 flex-grow">
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-300 text-sm font-light">
                    3D Generative Renders
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-300 text-sm font-light">
                    Live Supply Chain Material Lists
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-300 text-sm font-light">
                    Automated Lighting Simulations
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                  <span className="text-gray-300 text-sm font-light">
                    Direct Material Bank Sourcing
                  </span>
                </li>
              </ul>
              <button className="w-full py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white hover:text-black transition-colors">
                Select The Refresh
              </button>
            </div>

            {/* Tier 2 */}
            <div className="bg-[#1c1c1e] rounded-[2rem] border border-white/20 p-10 flex flex-col relative transform lg:-translate-y-4 shadow-2xl h-[640px]">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white text-black px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-lg">
                Most Popular
              </div>
              <div className="mb-8 mt-2">
                <h3 className="text-3xl font-semibold text-white mb-2 tracking-tight">
                  The Modifier
                </h3>
                <div className="text-gray-400 text-sm font-light mb-6">
                  Structural Additions & Hardscaping
                </div>
                <div className="flex items-baseline text-4xl font-semibold text-white tracking-tighter">
                  $8,000
                  <span className="ml-1 text-base font-light text-gray-500 tracking-normal">
                    /project
                  </span>
                </div>
              </div>
              <ul className="space-y-5 mb-8 flex-grow">
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                  <span className="text-gray-200 text-sm font-light">
                    Everything in The Refresh
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                  <span className="text-gray-200 text-sm font-light">
                    Technical 2D Floor Plans
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                  <span className="text-gray-200 text-sm font-light">
                    Automated Building Permit Sets
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                  <span className="text-gray-200 text-sm font-light">
                    Site & Topography Integration
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                  <span className="text-gray-200 text-sm font-light">
                    Contractor Bid Set Generation
                  </span>
                </li>
              </ul>
              <button className="w-full py-4 rounded-full bg-white text-black font-medium hover:bg-gray-200 transition-colors">
                Select The Modifier
              </button>
            </div>

            {/* Tier 3 */}
            <div className="bg-[#111111] rounded-[2rem] border border-white/5 p-10 flex flex-col hover:bg-[#151515] transition-colors h-[600px]">
              <div className="mb-8">
                <h3 className="text-3xl font-semibold text-white mb-2 tracking-tight">
                  The Visionary
                </h3>
                <div className="text-gray-400 text-sm font-light mb-6">
                  Full-scale Ground-up Development
                </div>
                <div className="flex items-baseline text-4xl font-semibold text-white tracking-tighter">
                  Enterprise
                </div>
              </div>
              <ul className="space-y-5 mb-8 flex-grow">
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-400 text-sm font-light">
                    Everything in The Modifier
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-400 text-sm font-light">
                    Complete Property Acquisition Models
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-400 text-sm font-light">
                    Full Planning Design Review Packages
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <CheckCircle2 className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  <span className="text-gray-400 text-sm font-light">
                    Strategic Project Roadmaps
                  </span>
                </li>
              </ul>
              <button className="w-full py-4 rounded-full border border-white/10 text-white font-medium hover:bg-white/10 transition-colors">
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Add-Ons */}
      <section id="addons" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-24 text-center">
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tight text-white mb-6">
              Hardware & Add-Ons.
            </h2>
            <p className="text-xl text-gray-400 font-light max-w-2xl mx-auto tracking-tight">
              Enhance your capabilities with specialized modules designed for
              the modern builder.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#151515] transition-colors">
              <Glasses className="h-10 w-10 text-white mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-2 tracking-tight">
                Hardware Bundle
              </h3>
              <p className="text-[10px] text-gray-500 mb-6 font-semibold uppercase tracking-widest">
                Immersive Command
              </p>
              <p className="text-gray-400 font-light text-sm leading-relaxed mb-8">
                Step into a 1:1 scale environment with Apple Vision Pro
                integration. Use the included iPad Pro with LiDAR sensors to
                perform instant 3D scans of existing spaces.
              </p>
              <ul className="space-y-3">
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span> VR
                  Headset Walkthroughs
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  iPad Pro LiDAR Scanning
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Precision Peripherals
                </li>
              </ul>
            </div>

            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#151515] transition-colors">
              <Users className="h-10 w-10 text-white mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-2 tracking-tight">
                Concierge Services
              </h3>
              <p className="text-[10px] text-gray-500 mb-6 font-semibold uppercase tracking-widest">
                Human-in-the-Loop
              </p>
              <p className="text-gray-400 font-light text-sm leading-relaxed mb-8">
                While Nexus automates the paperwork, some hurdles require a
                human touch. Gain priority access to a curated network of vetted
                structural engineers to stamp your sets.
              </p>
              <ul className="space-y-3">
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Expert Structural Sign-Offs
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Expedited Permitting
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Dedicated Roadmap Managers
                </li>
              </ul>
            </div>

            <div className="bg-[#111111] p-10 rounded-[2rem] border border-white/5 hover:bg-[#151515] transition-colors">
              <Layers className="h-10 w-10 text-white mb-6" />
              <h3 className="text-2xl font-semibold text-white mb-2 tracking-tight">
                Portfolio Growth
              </h3>
              <p className="text-[10px] text-gray-500 mb-6 font-semibold uppercase tracking-widest">
                Property Multiplier
              </p>
              <p className="text-gray-400 font-light text-sm leading-relaxed mb-8">
                Designed for the institutional investor. Take a successful
                design and clone it across multiple locations instantly while
                adapting to local topography.
              </p>
              <ul className="space-y-3">
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  1-Click Design Cloning
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Jurisdictional Adaptation
                </li>
                <li className="text-sm font-light text-gray-300 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>{" "}
                  Bulk Permit Generation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Municipal Portal (B2G) */}
      <section
        id="municipal"
        className="py-32 bg-[#050505] border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-gray-300 text-[10px] font-semibold uppercase tracking-widest mb-8">
                <Building2 className="h-3.5 w-3.5" /> B2G Municipal Portal
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
                Transforming
                <br />
                City Planning.
              </h2>
              <p className="text-xl text-gray-400 font-light mb-10 leading-relaxed tracking-tight">
                From manual verification to automated approval. We help city
                planning departments clear the permit backlog crisis.
              </p>
              <ul className="space-y-8 mb-10">
                <li className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <ShieldCheck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium text-lg">
                      Automated Code Auditing
                    </h4>
                    <p className="text-gray-400 font-light text-sm mt-2 leading-relaxed">
                      Mathematically verifies submitted designs against
                      municipal zoning codes in real-time, instantly flagging
                      violations.
                    </p>
                  </div>
                </li>
                <li className="flex gap-5">
                  <div className="flex-shrink-0 mt-1">
                    <HardHat className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium text-lg">
                      Frictionless Revisions
                    </h4>
                    <p className="text-gray-400 font-light text-sm mt-2 leading-relaxed">
                      Click a specific wall in the 3D viewer, leave a comment,
                      and push the revision directly to the developer's
                      dashboard.
                    </p>
                  </div>
                </li>
              </ul>
              <button className="bg-white text-black px-8 py-3.5 rounded-full font-medium transition-colors hover:bg-gray-200">
                Become a Beta Partner City
              </button>
            </div>

            <div className="relative h-[500px] bg-black rounded-[2rem] border border-white/10 flex items-center justify-center overflow-hidden shadow-2xl">
              {/* Abstract City Review Dashboard UI */}
              <div className="absolute inset-6 border border-white/10 rounded-xl bg-[#0a0a0c] flex flex-col">
                <div className="h-12 border-b border-white/5 flex items-center px-5 justify-between bg-[#111111]">
                  <span className="text-[10px] text-gray-500 font-medium tracking-widest uppercase">
                    City Portal // Review Mode
                  </span>
                  <div className="flex gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ff453a]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#ffd60a]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#32d74b]"></div>
                  </div>
                </div>
                <div className="flex-grow p-6 flex gap-6">
                  <div className="w-2/3 border border-white/5 rounded-lg bg-black relative flex items-center justify-center">
                    {/* 3D Massing Abstract */}
                    <div className="w-32 h-32 bg-[#1c1c1e] border-2 border-[#32d74b]/50 relative"></div>
                    <div className="absolute top-4 right-4 bg-[#32d74b]/10 text-[#32d74b] text-[10px] font-medium tracking-widest px-3 py-1.5 border border-[#32d74b]/20 rounded-full">
                      COMPLIANT
                    </div>
                  </div>
                  <div className="w-1/3 flex flex-col gap-4">
                    <div className="flex-grow border border-white/5 rounded-lg bg-[#111111] p-5">
                      <div className="h-1.5 w-1/2 bg-white/20 rounded mb-5"></div>
                      <div className="h-1.5 w-full bg-white/10 rounded mb-3"></div>
                      <div className="h-1.5 w-3/4 bg-white/10 rounded mb-3"></div>
                      <div className="h-1.5 w-full bg-white/10 rounded mb-8"></div>

                      <div className="text-[10px] text-[#ff453a] mb-2 font-medium tracking-widest uppercase">
                        Violation Detected
                      </div>
                      <div className="h-20 w-full border border-[#ff453a]/30 bg-[#ff453a]/10 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA & Footer */}
      <footer
        id="start"
        className="bg-black pt-32 pb-16 relative overflow-hidden"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl md:text-7xl font-semibold tracking-tighter text-white mb-6">
            The Blueprint is Ready.
            <br />
            <span className="text-gray-500">Are You?</span>
          </h2>
          <p className="text-xl text-gray-400 font-light mb-12 tracking-tight">
            Stop coordinating. Start building. The gap between "dirt" and
            "doorstep" just closed.
          </p>
          <button
            onClick={() => setShowAIModal(true)}
            className="bg-white text-black px-10 py-5 rounded-full font-medium text-lg hover:bg-gray-200 transition-all mb-6 flex items-center justify-center gap-3 mx-auto"
          >
            Launch Your First Project
          </button>
          <p className="text-xs text-gray-500 font-light">
            No credit card required for your first 5 parcel lookups.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-40 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-2 mb-4 md:mb-0">
            <span className="font-semibold text-sm tracking-tight text-white">
              iDESIGN<span className="text-gray-500">DEVELOP</span>
            </span>
          </div>
          <div className="flex gap-8 text-xs text-gray-500 font-light">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Pricing FAQ
            </a>
          </div>
          <div className="mt-4 md:mt-0 text-xs text-gray-500 font-light">
            &copy; {new Date().getFullYear()} iDESIGNDEVELOP.COM. All rights
            reserved.
          </div>
        </div>
      </footer>

      {/* AI Assistant Modal (Apple Sheet Style) */}
      {showAIModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-2xl">
          <div className="bg-[#1c1c1e] border border-white/10 rounded-[2rem] w-full max-w-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/5">
              <div className="flex items-center gap-3">
                <Sparkles className="h-5 w-5 text-white" />
                <h3 className="text-lg font-medium text-white tracking-tight">
                  Nexus Intelligence
                </h3>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[#000] border border-white/10 rounded-full">
                  <ShoppingCart className="h-3.5 w-3.5 text-white" />
                  <span className="text-xs font-medium text-white">
                    {cart.reduce((acc, item) => acc + item.qty, 0)}
                  </span>
                </div>
                <button
                  onClick={() => setShowAIModal(false)}
                  className="bg-white/10 text-white rounded-full p-1.5 hover:bg-white/20 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Segmented Control Tabs */}
            <div className="px-6 pt-6">
              <div className="flex bg-black p-1 rounded-xl border border-white/5 overflow-x-auto">
                <button
                  onClick={() => {
                    setActiveAITab("feasibility");
                    setAiResponse(null);
                    setAiPrompt("");
                  }}
                  className={`flex-1 min-w-[120px] py-2 px-2 text-[11px] uppercase tracking-wider font-semibold rounded-lg transition-all duration-300 flex justify-center items-center gap-2 ${
                    activeAITab === "feasibility"
                      ? "bg-[#2c2c2e] text-white shadow"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  <Map className="h-3 w-3" /> Feasibility
                </button>
                <button
                  onClick={() => {
                    setActiveAITab("material");
                    setAiResponse(null);
                    setAiPrompt("");
                  }}
                  className={`flex-1 min-w-[120px] py-2 px-2 text-[11px] uppercase tracking-wider font-semibold rounded-lg transition-all duration-300 flex justify-center items-center gap-2 ${
                    activeAITab === "material"
                      ? "bg-[#2c2c2e] text-white shadow"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  <Package className="h-3 w-3" /> Materials
                </button>
                <button
                  onClick={() => {
                    setActiveAITab("proposal");
                    setAiResponse(null);
                    setAiPrompt("");
                  }}
                  className={`flex-1 min-w-[120px] py-2 px-2 text-[11px] uppercase tracking-wider font-semibold rounded-lg transition-all duration-300 flex justify-center items-center gap-2 ${
                    activeAITab === "proposal"
                      ? "bg-[#2c2c2e] text-white shadow"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  <FileText className="h-3 w-3" /> Pitch Deck
                </button>
                <button
                  onClick={() => {
                    setActiveAITab("code");
                    setAiResponse(null);
                    setAiPrompt("");
                  }}
                  className={`flex-1 min-w-[120px] py-2 px-2 text-[11px] uppercase tracking-wider font-semibold rounded-lg transition-all duration-300 flex justify-center items-center gap-2 ${
                    activeAITab === "code"
                      ? "bg-[#2c2c2e] text-white shadow"
                      : "text-gray-500 hover:text-gray-300"
                  }`}
                >
                  <MessageSquare className="h-3 w-3" /> Zoning Guru
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-grow">
              <p className="text-gray-400 text-sm font-light mb-4">
                {getPlaceholderText()}
              </p>

              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="Enter prompt..."
                className="w-full h-24 bg-black border border-white/10 rounded-2xl p-4 text-white font-light placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors resize-none mb-4 text-sm"
              />

              <button
                onClick={handleAIGeneration}
                disabled={isGenerating || !aiPrompt.trim()}
                className="w-full py-3.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-all bg-[#2997ff] text-white hover:bg-[#147ce5] disabled:bg-white/5 disabled:text-gray-600 text-sm"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Processing...
                  </>
                ) : (
                  <>{getButtonText()}</>
                )}
              </button>

              {aiResponse && (
                <div className="mt-8 animate-in slide-in-from-bottom-4 fade-in duration-500">
                  {activeAITab === "feasibility" &&
                  typeof aiResponse === "object" ? (
                    <div className="space-y-4">
                      {/* Visual Location Overlay */}
                      <div className="relative h-64 bg-black border border-white/10 rounded-2xl overflow-hidden group">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:24px_24px]"></div>

                        {/* 3D Envelope */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="relative w-40 h-40 transform transition-transform duration-1000 group-hover:rotate-6 group-hover:scale-105">
                            <div className="absolute bottom-0 w-full h-1/2 border border-white/20 bg-white/5 transform skew-x-12"></div>
                            <div className="absolute inset-4 border border-white/40 bg-white/5 backdrop-blur-sm shadow-[0_0_30px_rgba(255,255,255,0.05)]"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_10px_#ffffff] animate-ping"></div>
                          </div>
                        </div>

                        {/* Overlays */}
                        <div className="absolute top-4 left-4 bg-white/10 backdrop-blur-md px-3 py-2 border border-white/10 rounded-xl">
                          <div className="text-[9px] text-gray-400 font-medium tracking-widest uppercase mb-1">
                            Zoning
                          </div>
                          <div className="text-sm text-white font-medium">
                            {aiResponse.zoningDetails?.code}
                          </div>
                        </div>

                        <div className="absolute bottom-4 right-4 bg-white/10 backdrop-blur-md px-3 py-2 border border-white/10 rounded-xl text-right">
                          <div className="text-[9px] text-gray-400 font-medium tracking-widest uppercase mb-1">
                            Constraints
                          </div>
                          <div className="text-xs text-white font-light leading-relaxed">
                            Max Ht: {aiResponse.zoningDetails?.maxHeight}
                            <br />
                            FAR: {aiResponse.zoningDetails?.far}
                            <br />
                            Setbacks: {aiResponse.zoningDetails?.setbacks}
                          </div>
                        </div>
                      </div>

                      {/* Analysis & Next Steps */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-black border border-white/10 rounded-2xl p-5">
                          <h4 className="text-white font-medium text-sm mb-3">
                            Analysis
                          </h4>
                          <p className="text-gray-400 text-xs font-light leading-relaxed mb-4">
                            {aiResponse.analysis}
                          </p>
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1c1c1e] rounded-lg border border-white/5">
                            <span className="text-[10px] text-gray-500 uppercase font-medium tracking-wider">
                              Tier:
                            </span>
                            <span className="text-white text-xs font-medium">
                              {aiResponse.recommendedTier}
                            </span>
                          </div>
                        </div>

                        <div className="bg-black border border-white/10 rounded-2xl p-5">
                          <h4 className="text-white font-medium text-sm mb-3">
                            Roadmap
                          </h4>
                          <ul className="space-y-4">
                            {aiResponse.nextSteps?.map((step, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <div className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-white/10 flex items-center justify-center">
                                  <span className="text-white text-[9px] font-bold">
                                    {idx + 1}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-400 font-light leading-snug">
                                  {step}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : activeAITab === "material" &&
                    typeof aiResponse === "object" ? (
                    <div className="space-y-4">
                      <div className="bg-black border border-white/10 rounded-2xl p-5 text-center">
                        <h4 className="text-white font-medium text-base mb-1">
                          {aiResponse.collectionName}
                        </h4>
                        <p className="text-gray-400 text-xs font-light">
                          {aiResponse.description}
                        </p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        {aiResponse.products?.map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            onAdd={addToCart}
                          />
                        ))}
                      </div>
                    </div>
                  ) : activeAITab === "proposal" &&
                    typeof aiResponse === "object" ? (
                    <div className="space-y-4">
                      <div className="bg-black border border-white/10 rounded-2xl p-6 text-center">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/10 mb-4">
                          <TrendingUp className="h-3 w-3 text-gray-400" />
                          <span className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">
                            Investment Teaser
                          </span>
                        </div>
                        <h4 className="text-white font-semibold text-xl md:text-2xl tracking-tight mb-4">
                          {aiResponse.projectTitle}
                        </h4>
                        <p className="text-gray-400 text-sm font-light leading-relaxed max-w-xl mx-auto">
                          {aiResponse.executiveSummary}
                        </p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-black border border-white/10 rounded-2xl p-5">
                          <h5 className="text-[10px] text-gray-500 font-medium tracking-widest uppercase mb-3">
                            Target Demographic
                          </h5>
                          <p className="text-gray-300 text-sm font-light leading-relaxed">
                            {aiResponse.targetDemographic}
                          </p>
                        </div>
                        <div className="bg-black border border-white/10 rounded-2xl p-5">
                          <h5 className="text-[10px] text-gray-500 font-medium tracking-widest uppercase mb-3 flex items-center gap-2">
                            <Clock className="h-3 w-3" /> Estimated Timeline
                          </h5>
                          <p className="text-gray-300 text-sm font-light leading-relaxed">
                            {aiResponse.estimatedTimeline}
                          </p>
                        </div>
                      </div>

                      <div className="bg-black border border-white/10 rounded-2xl p-5">
                        <h5 className="text-[10px] text-gray-500 font-medium tracking-widest uppercase mb-4">
                          Projected ROI Highlights
                        </h5>
                        <ul className="space-y-3">
                          {aiResponse.roiHighlights?.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <CheckCircle2 className="h-4 w-4 text-white flex-shrink-0 mt-0.5" />
                              <span className="text-gray-300 text-sm font-light">
                                {highlight}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : activeAITab === "code" ? (
                    <div className="bg-black border border-white/10 rounded-2xl p-6">
                      <div className="flex gap-4 items-start">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white flex items-center justify-center">
                          <ShieldCheck className="h-4 w-4 text-black" />
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-white mb-2">
                            Code Consultant
                          </h5>
                          <p className="text-gray-400 text-sm font-light leading-relaxed whitespace-pre-wrap">
                            {aiResponse}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
